import { AnyAction, ListenerEffectAPI, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { unwrapQueryActionResult } from '@redux/queries/unwrap'
import { setProductUpdateInProgress } from '@redux/slices/connectorSlice'
import { authApi } from '@redux/queries/auth'
import {
  pollClaimRecordController,
  PollClaimRecordControllerReturn,
} from '@hooks/usePollClaimRecord/pollClaimRecordController'
import { isProductUpdateRequired, isPollCartStatus } from './cartProductUtil'

const POLLING = {
  pollingInterval: 1000,
  maxWaitTime: 15000,
}

type ListenerApi = ListenerEffectAPI<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>
type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * Poll claim record on token fetch to trigger product update
 */
export const initCartProductUpdateOnTokenFetchListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: authApi.endpoints.getAuthToken.matchFulfilled,

    effect: async (action, listenerApi) => {
      const authToken = action.payload
      const state = listenerApi.getState() as AppState
      const isInProgress = state.connector.isProductUpdateInProgress

      if (!authToken?.claimRecordId || isInProgress) return

      listenerApi.dispatch(setProductUpdateInProgress(true))

      try {
        await pollThenUpdate(listenerApi, authToken.claimRecordId)
      } catch (error) {
        console.warn('directbilling - update product error', error)
      } finally {
        listenerApi.dispatch(setProductUpdateInProgress(false))
      }
    },
  })
}

const pollThenUpdate = async (listenerApi: ListenerApi, claimRecordId: string): Promise<void> => {
  const handlers = {
    isReady: (claimRecord: ClaimRecord) => {
      return !isPollCartStatus(claimRecord?.status) || isProductUpdateRequired(claimRecord.cartStatus)
    },
    onReady: async (_claimRecord: ClaimRecord) => {
      // fetch claim record again to trigger cartProductUpdateListener
      safeFetchClaimRecord(listenerApi, claimRecordId)
    },
    onTimeExceeded: (_claimRecord: ClaimRecord) => {
      // noop
    },
    onError: (error?: unknown) => {
      console.warn('directbilling - polling error', error)
    },
  }

  const poller = getPollClaimRecordController(listenerApi, claimRecordId)
  await poller.start(handlers, claimRecordId)
}

const getPollClaimRecordController = (
  listenerApi: ListenerApi,
  claimRecordId: string
): PollClaimRecordControllerReturn => {
  // Note: using the claim record fetch that does not trigger the cartProductUpdateListener
  const service = {
    fetchClaimRecord: () => safeFetchClaimRecordForPolling(listenerApi, claimRecordId),
  }

  const params = {
    maxWaitTime: POLLING.maxWaitTime,
    pollingInterval: POLLING.pollingInterval,
  }

  return pollClaimRecordController(service, params)
}

const safeFetchClaimRecord = async (listenerApi: ListenerApi, claimRecordId: string): Promise<ClaimRecord | null> => {
  try {
    return await unwrapQueryActionResult<ClaimRecord>(
      listenerApi.dispatch(claimRecordsApi.endpoints.getClaimRecord.initiate(claimRecordId, { forceRefetch: true }))
    )
  } catch (error) {
    console.warn('directbilling - update products not performed, error fetching claim record', error)
    return null
  }
}

const safeFetchClaimRecordForPolling = async (
  listenerApi: ListenerApi,
  claimRecordId: string
): Promise<ClaimRecord | null> => {
  try {
    return await unwrapQueryActionResult<ClaimRecord>(
      listenerApi.dispatch(
        claimRecordsApi.endpoints.getClaimRecordNoCache.initiate(claimRecordId, { forceRefetch: true })
      )
    )
  } catch (error) {
    console.warn('directbilling - update products not performed, error fetching claim record', error)
    return null
  }
}
